/** One stage panel of the order spine (plan §2.4) — the records core's `RecordPanel`
 *  (Editable fields, the muted "From <restech>" vendor block, the System block) over the
 *  order's FieldSlots, with its identity grid. The panel is the stage's anchor — the
 *  spine nodes and `1`–`4` scroll to it and focus it. */
import type { ReactNode } from 'react';

import type { OrderRecordStage } from '@/api/orderRecord';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { FieldCellGrid } from '@/features/records/fields/FieldCellGrid';
import { FieldSlot } from '@/features/records/fields/FieldSlot';
import { FIELD_GRID_SX, PANEL_INSET_PX } from '@/features/records/panels/fieldGrid';
import { RecordPanel, type RecordFieldGroup } from '@/features/records/panels/RecordPanel';
import type { ViewFieldRef, ViewSection } from '@/features/records/views/types';

import { orderStageAnchorId } from '../views/spineLayout';

import { orderEditableDisplay, orderFieldActions, orderFieldDisplay, type OrderDisplayContext } from './displays';

/** Widgets whose input wants the whole row. */
const WIDE_WIDGETS = new Set(['tags']);
/** Read-only values that read as lists. */
const WIDE_KEYS = new Set(['bookings', 'prices']);
/** Pickers whose identity line (`Name · #pk · TENANT` — what tells two accounts of one
 *  name apart) needs two field columns: the account. */
const TWO_COLUMN_WIDGETS = new Set(['user']);

/** Room for the D10 identity fields: their warnings and the linked-bookings question sit
 *  under them, so they take at least 260 px each — two up when the panel allows. */
const IDENTITY_MIN_PX = 260;
const IDENTITY_GAP_PX = 16;

/** Set on a panel's ancestor to pin the identity grid's column count, like
 *  `FIELD_GRID_VAR` pins the field grid's (`identityGridColumns`) — entering edit mode
 *  never moves the machine ID under the order ref. */
export const IDENTITY_GRID_VAR = '--order-identity-grid';

export function identityGridColumns(panelWidth: number): string {
  const fit = Math.floor((panelWidth - PANEL_INSET_PX + IDENTITY_GAP_PX) / (IDENTITY_MIN_PX + IDENTITY_GAP_PX));
  return `repeat(${Math.min(2, Math.max(1, fit))}, minmax(0, 1fr))`;
}

const IDENTITY_GRID_SX = {
  ...FIELD_GRID_SX,
  gridTemplateColumns: `var(${IDENTITY_GRID_VAR}, repeat(auto-fill, minmax(${IDENTITY_MIN_PX}px, 1fr)))`,
} as const;

export interface OrderFieldGroup extends RecordFieldGroup {
  /** `identity` — the order ref / machine ID grid. */
  layout?: 'fields' | 'identity';
}

export interface OrderStagePanelProps {
  section: ViewSection<OrderRecordStage>;
  display: OrderDisplayContext;
  /** Right side of the panel header ("Times in UTC"). */
  meta?: ReactNode;
  /** Renderings that replace the generic one for a key (the bookings list). */
  displays?: Partial<Record<string, ReactNode>>;
  /** Label overrides by key ("3 linked bookings"). */
  labels?: Partial<Record<string, string>>;
  /** Sub-groups of the editable block. Default: one group. */
  groupEditable?: (fields: ViewFieldRef[]) => OrderFieldGroup[];
  /** The system block's heading (default "System · derived and provenance"). */
  systemTitle?: { title: string; note?: string };
  afterEditable?: ReactNode;
  afterSystem?: ReactNode;
}

export function OrderStagePanel({ section, display, displays, labels, ...rest }: OrderStagePanelProps) {
  return (
    <RecordPanel
      section={section}
      anchorId={orderStageAnchorId(section.id)}
      renderFields={(g) => (
        <OrderFieldGrid fields={g.fields} display={display} displays={displays} labels={labels} identity={g.layout === 'identity'} />
      )}
      {...rest}
    />
  );
}

function OrderFieldGrid({
  fields,
  display,
  displays,
  labels,
  identity = false,
}: {
  fields: ViewFieldRef[];
  display: OrderDisplayContext;
  displays?: Partial<Record<string, ReactNode>> | undefined;
  labels?: Partial<Record<string, string>> | undefined;
  identity?: boolean;
}) {
  const { fieldByKey } = useRecordEditor();
  return (
    <FieldCellGrid
      sx={identity ? IDENTITY_GRID_SX : FIELD_GRID_SX}
      cells={fields.map((f) => {
        const field = fieldByKey.get(f.key);
        const readOnly = field?.kind === 'vendor' || field?.kind === 'derived';
        const override = displays?.[f.key];
        const shown =
          override !== undefined
            ? override
            : readOnly
              ? orderFieldDisplay(f.key, display)
              : orderEditableDisplay(f.key, display);
        const actions = readOnly ? undefined : orderFieldActions(f.key, display);
        const label = labels?.[f.key];
        // An input keeps its width in both modes; a read-only list takes the row only when
        // it has something to list.
        const wide =
          Boolean(field?.widget && WIDE_WIDGETS.has(field.widget)) ||
          (WIDE_KEYS.has(f.key) && shown !== null && shown !== undefined && shown !== '');
        return {
          key: `${f.entity}:${f.id}:${f.key}`,
          rec: `${f.entity}:${f.id}`,
          path: f.path,
          wide,
          // In both modes: Edit moves nothing.
          ...(field?.widget && TWO_COLUMN_WIDGETS.has(field.widget) ? { span: 2 } : {}),
          node: (
            <FieldSlot
              fieldKey={f.key}
              entity={f.entity}
              id={f.id}
              path={f.path}
              inlineChoices={false}
              {...(label ? { label } : {})}
              {...(shown !== undefined ? { display: shown } : {})}
              {...(actions ? { actions } : {})}
            />
          ),
        };
      })}
    />
  );
}
